import React from 'react';
import PropTypes from 'prop-types';
import { Box } from 'grommet';
import { useAppTheme } from '@Components/Context';
import { AppButton, DropdownSort, FlexibleSelectDropdown } from '@Components/Control';
import { ProductFormCheckBoxInput } from '@Components/Partial/Product/NewSearch';
import { SearchInput } from '../SharedComponents';
import DateFilterDropdown from './DateFilterDropdown';

const ListTableControls = ({
  small,
  query,
  onQueryChange,
  onClickExport,
  showHideCompanyChannelVideos,
  assetLabels,
  searchPlaceholder,
  statusOptions,
  statusLabel,
  typeOptions,
  typeLabel,
  sortOptions,
  dateFilterOptions,
  paymentPending = false,
}) => {
  const { theme: { page, button } } = useAppTheme();
  const assetLabelOptions = (assetLabels || []).map((label) => ({ display: label, api: label }));

  const handleDateChange = (key, date) => {
    onQueryChange(key, date);
  };

  const handleExport = () => {
    onClickExport();
  };

  const handleResetFilters = () => {
    onQueryChange('reset');
  };

  // New sort handlers
  const handleSortFieldChange = (field, direction) => {
    onQueryChange('sortField', field);
    onQueryChange('sortOrder', direction);
  };

  const handleSortDirectionToggle = () => {
    const newDirection = query.sortOrder === 'asc' ? 'desc' : 'asc';
    onQueryChange('sortOrder', newDirection);
  };

  return (
    <Box wrap direction="row" gap="0.5rem" align="center">
      {showHideCompanyChannelVideos && (
        <Box pad={{ vertical: small ? '0.5rem' : '0', right: '0.25rem' }}>
          <ProductFormCheckBoxInput
            small={small}
            bgColor={page.inputBg}
            borderColor={page.inputBorder}
            checkmarkColor={page.highlight}
            focusHighlight={page.focusHighlight}
            value={query.hideCompanyChannelVideos || false}
            handleFormValues={(val) => onQueryChange('hideCompanyChannelVideos', val)}
            label="Hide my channel's videos"
            labelSize="0.9rem"
            customBoxSize="1.5rem"
            customCheckmarkSize="1.35rem"
          />
        </Box>
      )}
      {searchPlaceholder && (
        <Box pad={{ vertical: small ? '0.5rem' : '0' }}>
          <SearchInput
            key="search"
            customPlaceholder={searchPlaceholder}
            small={small}
            handleSearch={(_, value) => onQueryChange('q', value)}
            initialValue={query.q || ''}
          />
        </Box>
      )}
      {assetLabelOptions.length > 1 && (
        <Box pad={{ vertical: small ? '0.5rem' : '0' }}>
          <FlexibleSelectDropdown
            textColor={page.inputText}
            handleItemSelect={(value) => onQueryChange('assetLabel', value)}
            flexibleOptions={assetLabelOptions}
            noSelectionLabel="All Asset Labels"
            displayKey="display"
            valueKey="api"
            iconPath="images/filter-input-icon.svg"
            customPad="0.2rem 0.3rem"
            selectedOption={
              assetLabelOptions.find((opt) => opt.api === query.assetLabel)?.display || 'All Asset Labels'
            }
            withReset={!!query.assetLabel}
          />
        </Box>
      )}
      {statusOptions && (
        <Box pad={{ vertical: small ? '0.5rem' : '0' }}>
          <FlexibleSelectDropdown
            textColor={page.inputText}
            handleItemSelect={(value) => onQueryChange('status', value)}
            flexibleOptions={statusOptions}
            noSelectionLabel={statusLabel}
            displayKey="display"
            valueKey="api"
            iconPath="images/filter-input-icon.svg"
            customPad="0.2rem 0.3rem"
            selectedOption={
              statusOptions.find((opt) => opt.api === query.status)?.display || statusLabel
            }
            withReset={!!query.status}
          />
        </Box>
      )}
      {typeOptions && (
        <Box pad={{ vertical: small ? '0.5rem' : '0' }}>
          <FlexibleSelectDropdown
            textColor={page.inputText}
            handleItemSelect={(value) => onQueryChange('type', value)}
            flexibleOptions={typeOptions}
            noSelectionLabel={typeLabel}
            displayKey="display"
            valueKey="api"
            iconPath="images/filter-input-icon.svg"
            customPad="0.2rem 0.3rem"
            selectedOption={
              typeOptions.find((opt) => opt.api === query.type)?.display || typeLabel
            }
            withReset={!!query.type}
          />
        </Box>
      )}
      {dateFilterOptions && (
        <Box pad={{ vertical: small ? '0.5rem' : '0' }}>
          <DateFilterDropdown
            small={small}
            query={query}
            onDateSelect={handleDateChange}
            onQueryChange={onQueryChange}
            dateFilterOptions={dateFilterOptions}
          />
        </Box>
      )}
      {sortOptions && (
        <Box pad={{ vertical: small ? '0.5rem' : '0' }}>
          <DropdownSort
            sortOptions={sortOptions}
            selectedSortField={query.sortField}
            sortDirection={query.sortOrder || 'desc'}
            onSortFieldChange={handleSortFieldChange}
            onSortDirectionToggle={handleSortDirectionToggle}
            textColor={page.inputText}
            iconColor={page.inputIcon}
            buttonBg={page.inputBg}
            customPad="0.35rem 0.3rem"
          />
        </Box>
      )}
      {Boolean(onClickExport) && (
        <Box pad={{ vertical: small ? '0.5rem' : '0' }}>
          <AppButton
            overrideHover
            onClick={handleExport}
            level="tableControls"
            color={button.primaryText}
            bgColor={button.primaryBg}
            height="var(--table-controls-height)"
            label="Export CSV"
            disabled={paymentPending}
          />
        </Box>
      )}
      <Box pad={{ vertical: small ? '0.5rem' : '0' }}>
        <AppButton
          overrideHover
          disabled={Object.values(query).every((v) => !v)}
          onClick={handleResetFilters}
          level="tableControls"
          height="var(--table-controls-height)"
          color={button.primaryText}
          bgColor={button.primaryBg}
          label="Reset"
        />
      </Box>
    </Box>
  );
};

ListTableControls.propTypes = {
  small: PropTypes.bool.isRequired,
  query: PropTypes.object.isRequired,
  onQueryChange: PropTypes.func.isRequired,
  onClickExport: PropTypes.func,
  searchPlaceholder: PropTypes.string,
  statusOptions: PropTypes.array,
  statusLabel: PropTypes.string,
  typeOptions: PropTypes.array,
  typeLabel: PropTypes.string,
  sortOptions: PropTypes.array,
  dateFilterOptions: PropTypes.array,
  paymentPending: PropTypes.bool,
  showHideCompanyChannelVideos: PropTypes.bool,
  assetLabels: PropTypes.array,
};

export default ListTableControls;
